import { Link } from 'react-router-dom';
import { Shield, Download, Palette, FileText, Image, CheckCircle } from 'lucide-react';

const BrandPage = () => {
  const brandColors = [
    { name: 'SAFE Navy', hex: '#0F172A', rgb: 'rgb(15, 23, 42)', usage: 'Primary brand color, headers, text' },
    { name: 'SAFE Teal', hex: '#0E7490', rgb: 'rgb(14, 116, 144)', usage: 'Accent color, SAFE Labs, links' },
    { name: 'SAFE Amber', hex: '#D97706', rgb: 'rgb(217, 119, 6)', usage: 'Call-to-action, SAIL, highlights' },
    { name: 'SAFE Paper', hex: '#F8FAFC', rgb: 'rgb(248, 250, 252)', usage: 'Backgrounds, light sections' },
    { name: 'SAFE White', hex: '#FFFFFF', rgb: 'rgb(255, 255, 255)', usage: 'Cards, content areas' },
  ];

  return (
    <div data-testid="brand-page">
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-cyan-400 mb-4">Resources</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-white mb-6">
            Brand & Media Resources
          </h1>
          <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl">
            Guidelines and assets for partners, media, and organizations using the SAFE brand in communications and publications.
          </p>
        </div>
      </section>

      {/* Brand Guidelines */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-cyan-700 mb-4">Brand Identity</p>
          <h2 className="heading-2 mb-8">Our Brand</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            <div>
              <p className="body-large mb-6">
                The SAFE brand represents trust, governance, and protection. Our visual identity is deliberately institutional — professional, credible, and authoritative.
              </p>
              <div className="space-y-4">
                {[
                  'Use official assets only — do not recreate or modify',
                  'Maintain clear space around the logo',
                  'Never distort, rotate, or change logo colors',
                  'Use on appropriate backgrounds per guidelines'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-cyan-700 flex-shrink-0 mt-0.5" />
                    <p className="text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 p-8">
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-6">Typography</p>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Primary Font</p>
                  <p className="text-2xl font-semibold" style={{ fontFamily: 'Public Sans, sans-serif' }}>Public Sans</p>
                  <p className="text-sm text-slate-500 mt-2">Used for headings and body text</p>
                </div>
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-500 mb-1">Monospace Font</p>
                  <p className="text-xl" style={{ fontFamily: 'JetBrains Mono, monospace' }}>JetBrains Mono</p>
                  <p className="text-sm text-slate-500 mt-2">Used for code and technical content</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logo Variants */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-cyan-700 mb-4">Logo</p>
          <h2 className="heading-2 mb-8">Logo Variants</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="border border-slate-200 overflow-hidden">
              <div className="bg-white h-64 flex items-center justify-center p-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_safe-kids-ai-1/artifacts/vibk77eo_safe_standard_blue_flat.png"
                  alt="Safe Standard Logo — Flat"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <div className="bg-white p-4 border-t border-slate-200 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900">Flat Logo</p>
                  <p className="text-sm text-slate-500">Blue on blue background</p>
                </div>
                <a
                  href="https://customer-assets.emergentagent.com/job_safe-kids-ai-1/artifacts/vibk77eo_safe_standard_blue_flat.png"
                  download="safe_standard_blue_flat.png"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm font-medium text-cyan-700 hover:text-cyan-900"
                >
                  <Download className="w-4 h-4" />
                  PNG
                </a>
              </div>
            </div>
            <div className="border border-slate-200 overflow-hidden">
              <div className="bg-slate-900 h-64 flex items-center justify-center p-8">
                <img
                  src="https://customer-assets.emergentagent.com/job_safe-kids-ai-1/artifacts/m333wys6_safe_standard_blue_gradient.png"
                  alt="Safe Standard Logo — Gradient"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <div className="bg-white p-4 border-t border-slate-200 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900">Gradient Logo</p>
                  <p className="text-sm text-slate-500">Blue gradient on dark background</p>
                </div>
                <a
                  href="https://customer-assets.emergentagent.com/job_safe-kids-ai-1/artifacts/m333wys6_safe_standard_blue_gradient.png"
                  download="safe_standard_blue_gradient.png"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm font-medium text-cyan-700 hover:text-cyan-900"
                >
                  <Download className="w-4 h-4" />
                  PNG
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Guide Download */}
      <section className="py-12 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-slate-50 border border-slate-200 p-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Safe Standard Brand Guide</p>
                <p className="text-sm text-slate-500">Complete brand guidelines — PDF</p>
              </div>
            </div>
            <a
              href="https://customer-assets.emergentagent.com/job_safe-kids-ai-1/artifacts/6fk20mu8_Safe_Standard_Brand_Guide.pdf"
              download="Safe_Standard_Brand_Guide.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download Brand Guide
            </a>
          </div>
        </div>
      </section>

      {/* Color Palette */}
      <section className="section-padding bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-cyan-700 mb-4">Colors</p>
          <h2 className="heading-2 mb-8">Brand Color Palette</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {brandColors.map((color, index) => (
              <div key={index} className="border border-slate-200 overflow-hidden">
                <div 
                  className="h-24" 
                  style={{ backgroundColor: color.hex }}
                ></div>
                <div className="bg-white p-4">
                  <p className="font-semibold text-slate-900">{color.name}</p>
                  <p className="text-sm text-slate-500 font-mono">{color.hex}</p>
                  <p className="text-sm text-slate-500 font-mono">{color.rgb}</p>
                  <p className="text-xs text-slate-400 mt-2">{color.usage}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Usage Guidelines */}
      <section className="section-padding bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto container-padding">
          <p className="caption text-cyan-700 mb-4">Usage</p>
          <h2 className="heading-2 mb-8">Brand Usage Guidelines</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
                <h3 className="text-lg font-semibold text-slate-900">Do</h3>
              </div>
              <ul className="space-y-3 text-slate-700">
                <li>• Use official logo files provided</li>
                <li>• Maintain minimum clear space</li>
                <li>• Use on approved background colors</li>
                <li>• Reference SAFE in editorial content accurately</li>
                <li>• Link to official SAFE website when mentioning</li>
              </ul>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-6 h-6 text-red-600" />
                <h3 className="text-lg font-semibold text-slate-900">Don't</h3>
              </div>
              <ul className="space-y-3 text-slate-700">
                <li>• Alter logo colors or proportions</li>
                <li>• Add effects (shadows, gradients, outlines)</li>
                <li>• Use logo on busy or low-contrast backgrounds</li>
                <li>• Imply endorsement without authorization</li>
                <li>• Use SAFE name in product branding without permission</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="section-padding bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto container-padding text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
            Need Brand Assets?
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
            For additional formats, custom usage requests, or media inquiries, please contact our communications team.
          </p>
          <p className="text-cyan-400 font-semibold">governance@safestandard.ai</p>
        </div>
      </section>
    </div>
  );
};

export default BrandPage;
